import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { AccountCircle, Menu as MenuIcon, MoreHoriz } from '@mui/icons-material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLayoutPreferences } from '../contexts/LayoutPreferencesContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useNavOverflow } from '../hooks/useNavOverflow';
import { useNavPreferences } from '../hooks/useNavPreferences';
import NavSettingsDialog from './NavSettingsDialog';

const navItems = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Habits', path: '/habits' },
  { label: 'Review', path: '/habits/review' },
  { label: 'Check-In', path: '/check-in' },
  { label: 'Commitments', path: '/commitments' },
  { label: 'Timelines', path: '/timelines' },
  { label: 'Countdowns', path: '/timers' },
  { label: 'Reflections', path: '/reflections' },
  { label: 'Projects', path: '/projects' },
  { label: 'Planner', path: '/planner' },
  { label: 'Calendar', path: '/calendar' },
  { label: 'Work Timers', path: '/work-timers' },
  { label: 'Planned Schedule', path: '/planned-events' },
  { label: 'Time Entries', path: '/time-entries' },
  { label: 'Reports', path: '/reports' },
  { label: 'Integrations', path: '/integrations' },
  { label: 'Exports', path: '/exports' },
  { label: 'Activity', path: '/activity' },
  { label: 'Help', path: '/help' },
];

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const preferences = usePreferences();
  const { layoutDensity, setLayoutDensity } = useLayoutPreferences();
  const {
    orderedItems,
    visibleItems,
    toggleHidden,
    moveUp,
    moveDown,
    resetToDefault,
  } = useNavPreferences(navItems);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const compactNav = useMediaQuery(theme.breakpoints.down('lg'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [navAnchorEl, setNavAnchorEl] = React.useState<null | HTMLElement>(null);
  const [overflowAnchorEl, setOverflowAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [navSettingsOpen, setNavSettingsOpen] = React.useState(false);

  // Desktop overflow folding: items that do not fit fold into a trailing menu.
  // The current route's item is promoted inline if it would otherwise fold.
  const activeIndex = visibleItems.findIndex(
    (item) =>
      location.pathname === item.path ||
      location.pathname.startsWith(`${item.path}/`)
  );
  const {
    containerRef,
    setItemRef,
    visible: inlineItems,
    overflow: overflowItems,
  } = useNavOverflow(visibleItems, activeIndex);

  const handleNavigate = (path: string) => {
    navigate(path);
    setNavAnchorEl(null);
  };

  const handleNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNavAnchorEl(event.currentTarget);
  };

  const handleOverflowMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOverflowAnchorEl(event.currentTarget);
  };

  const handleOverflowNavigate = (path: string) => {
    navigate(path);
    setOverflowAnchorEl(null);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const toggleCompactLayout = () => {
    setLayoutDensity(layoutDensity === 'compact' ? 'comfortable' : 'compact');
    handleClose();
  };

  const handleOpenNavSettings = () => {
    setNavSettingsOpen(true);
    handleClose();
  };

  return (
    <>
    <AppBar position="static">
      <Toolbar>
        <Typography
          variant="h6"
          component="div"
          noWrap
          sx={{ flexGrow: 1, cursor: 'pointer', minWidth: 0 }}
          onClick={() => navigate('/')}
        >
          Kalpa Niyama
        </Typography>
        
        {/* flexGrow feeds the toolbar's slack into the nav-items container so
            its measured width tracks window resizes in both directions;
            justifyContent keeps the icons right-aligned on mobile, where no
            child grows. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
            justifyContent: 'flex-end',
            minWidth: 0,
            gap: { xs: 0.5, lg: 2 },
          }}
        >
          {preferences?.syncStatus === 'error' && (
            <Tooltip title="Preferences will sync when the connection is restored">
              <CloudOffIcon fontSize="small" aria-label="Preferences not synced" />
            </Tooltip>
          )}
          {compactNav ? (
            <>
              <IconButton
                size="large"
                aria-label="open navigation"
                aria-controls="nav-menu"
                aria-haspopup="true"
                onClick={handleNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="nav-menu"
                anchorEl={navAnchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(navAnchorEl)}
                onClose={() => setNavAnchorEl(null)}
              >
                {visibleItems.map((item) => (
                  <MenuItem key={item.path} onClick={() => handleNavigate(item.path)}>
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <Box
              ref={containerRef}
              data-nav-container=""
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
                justifyContent: 'flex-end',
                gap: 2,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {inlineItems.map((item) => (
                <Button
                  key={item.path}
                  ref={setItemRef(item.path)}
                  data-nav-item=""
                  color="inherit"
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </Button>
              ))}
              {overflowItems.length > 0 && (
                <>
                  <IconButton
                    size="large"
                    aria-label="show more navigation"
                    aria-controls="nav-overflow-menu"
                    aria-haspopup="true"
                    onClick={handleOverflowMenu}
                    color="inherit"
                  >
                    <MoreHoriz />
                  </IconButton>
                  <Menu
                    id="nav-overflow-menu"
                    anchorEl={overflowAnchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(overflowAnchorEl)}
                    onClose={() => setOverflowAnchorEl(null)}
                  >
                    {overflowItems.map((item) => (
                      <MenuItem
                        key={item.path}
                        onClick={() => handleOverflowNavigate(item.path)}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" noWrap sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
              {user?.username}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleProfile}>Profile</MenuItem>
              <MenuItem onClick={toggleCompactLayout}>
                <ListItemText>Compact layout</ListItemText>
                <Switch
                  edge="end"
                  size="small"
                  checked={layoutDensity === 'compact'}
                  readOnly
                  tabIndex={-1}
                  inputProps={{ 'aria-label': 'Compact layout' }}
                  sx={{ ml: 2, pointerEvents: 'none' }}
                />
              </MenuItem>
              <MenuItem onClick={handleOpenNavSettings}>Nav settings…</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
    <NavSettingsDialog
      open={navSettingsOpen}
      onClose={() => setNavSettingsOpen(false)}
      items={orderedItems}
      onToggleHidden={toggleHidden}
      onMoveUp={moveUp}
      onMoveDown={moveDown}
      onReset={resetToDefault}
    />
    </>
  );
};

export default Navbar;
